'use client';

import { jsxs, jsx } from "react/jsx-runtime";
import { forwardRef, useState } from "react";
import { Text } from "../../atoms/Text/Text.js";
import { useId } from "../../utils/hooks/useId.js";
import { useResizeObserver } from "../../utils/hooks/useResizeObserver.js";
import { classNames } from "../../utils/styles/classNames.js";
import { chalkFocus } from "../../../css-utils.js";
import { scrollableContent } from "./KeyboardOnlyScrollable.data.js";
const KeyboardOnlyScrollable = forwardRef(
  // keyboard-only users generally need to move focus onto (or inside) an element in order to be able to scroll it;
  // if non-focusable container have no focusable elements - it requires a special handling to be scrollable by keyboard-only users.
  // see https://adrianroselli.com/2022/06/keyboard-only-scrolling-areas.html
  // eslint-disable-next-line prefer-arrow-callback -- we want to use function to provide a display name for the component
  function KeyboardOnlyScrollable2({
    children,
    onScrollChange,
    ...props
  }, forwardedRef) {
    const id = useId();
    const { hasScroll, ref } = useScrollableRef({
      forwardedRef,
      onScrollChange
    });
    return /* @__PURE__ */ jsxs(
      "div",
      {
        ...props,
        "aria-labelledby": hasScroll ? id : void 0,
        className: classNames(props.className, chalkFocus.low),
        ref,
        role: hasScroll ? "group" : void 0,
        tabIndex: hasScroll ? 0 : void 0,
        children: [
          hasScroll && /* @__PURE__ */ jsx(Text, { "aria-hidden": "true", id, visuallyHidden: true, children: scrollableContent }),
          children
        ]
      }
    );
  }
);
function useScrollableRef({
  forwardedRef,
  onScrollChange
}) {
  const [hasScroll, setHasScroll] = useState(false);
  const ref = useResizeObserver({
    forwardedRef,
    onResize(el) {
      const hasVScroll = el.scrollHeight > el.clientHeight;
      const hasHScroll = el.scrollWidth > el.clientWidth;
      setHasScroll(hasVScroll || hasHScroll);
      onScrollChange == null ? void 0 : onScrollChange(hasVScroll, hasHScroll);
    }
  });
  return { hasScroll, ref };
}
export {
  KeyboardOnlyScrollable
};
